import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PackageCheck, Truck, Tag, Clock } from "lucide-react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import AdminStatCard from "components/admin/AdminStatCard";
import StatusBadge from "components/admin/StatusBadge";
import CarteiraCliente from "components/admin/CarteiraCliente";
import { Button } from "components/ui/button";
import {
  AdminClienteDetail,
  getAdminClienteDetail,
  updateAdminClienteEnvios,
  updateAdminClienteStatus,
} from "services/admin/adminClientes.service";

const STATUS_TONE: Record<string, "green" | "yellow" | "red"> = {
  ativo: "green",
  suspenso: "yellow",
  inativo: "red",
};

const AdminClienteDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminClienteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    getAdminClienteDetail(id)
      .then(setDetail)
      .catch((err) => console.error("[AdminClienteDetalhe] erro:", err))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const handleEnviosChange = async (bloquear: boolean) => {
    if (!id) return;

    // O motivo aparece para o cliente na tela dele, entao vale perguntar em
    // vez de deixar a mensagem generica.
    let motivo: string | undefined;
    if (bloquear) {
      const digitado = window.prompt(
        "Motivo do bloqueio (o cliente vai ler isso ao tentar cotar):",
        "Pendência financeira. Fale com o suporte da Intrex.",
      );
      if (digitado === null) return;
      motivo = digitado.trim() || undefined;
    }

    setUpdating(true);
    try {
      await updateAdminClienteEnvios(id, bloquear, motivo);
      load();
    } catch (err) {
      console.error("[AdminClienteDetalhe] erro ao bloquear envios:", err);
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    setUpdating(true);
    try {
      await updateAdminClienteStatus(id, status);
      load();
    } catch (err) {
      console.error("[AdminClienteDetalhe] erro ao atualizar status:", err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !detail) {
    return (
      <AdminLayout title="Cliente" subtitle="Detalhes do cliente">
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </AdminLayout>
    );
  }

  const { cliente, integracoes, estatisticas, historico } = detail;

  return (
    <AdminLayout
      title={cliente.razaoSocial}
      subtitle="Detalhes do cliente"
      action={
        <Button variant="outline" onClick={() => navigate("/admin/clientes")}>
          Voltar
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <AdminCard
          title="Dados"
          action={
            <div className="flex items-center gap-2">
              <StatusBadge label={cliente.status} tone={STATUS_TONE[cliente.status] || "slate"} />
              {cliente.enviosBloqueados ? (
                <StatusBadge label="envios bloqueados" tone="red" />
              ) : null}
              <Button
                variant="outline"
                size="sm"
                disabled={updating}
                onClick={() => handleEnviosChange(!cliente.enviosBloqueados)}
              >
                {cliente.enviosBloqueados
                  ? "Liberar envios"
                  : "Bloquear envios"}
              </Button>
              <select
                disabled={updating}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                value=""
                onChange={(e) => e.target.value && handleStatusChange(e.target.value)}
              >
                <option value="">Alterar status...</option>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="suspenso">Suspenso</option>
              </select>
            </div>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <Field label="Nome" value={cliente.razaoSocial} />
            <Field label="CNPJ/CPF" value={cliente.cnpj} />
            <Field label="E-mail" value={cliente.emailPrincipal} />
            <Field label="Telefone" value={cliente.telefoneCelular} />
            <Field label="Plano" value={cliente.plano} />
            <Field label="Data de cadastro" value={new Date(cliente.createdAt).toLocaleDateString("pt-BR")} />
            {cliente.enviosBloqueados ? (
              <Field
                label="Envios bloqueados desde"
                value={
                  cliente.enviosBloqueadosEm
                    ? new Date(cliente.enviosBloqueadosEm).toLocaleDateString("pt-BR")
                    : "-"
                }
              />
            ) : null}
            {cliente.enviosBloqueados && cliente.enviosBloqueadosMotivo ? (
              <Field label="Motivo do bloqueio" value={cliente.enviosBloqueadosMotivo} />
            ) : null}
          </div>
        </AdminCard>

        {id ? <CarteiraCliente clienteId={id} /> : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <AdminCard title="Shopify">
            {integracoes.shopify.conectado ? (
              <div className="flex flex-col gap-2 text-sm">
                <Field label="Loja" value={integracoes.shopify.loja || "—"} />
                <Field label="Token válido" value={integracoes.shopify.tokenValido ? "Sim" : "Não"} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Não conectado.</p>
            )}
          </AdminCard>
          <AdminCard title="Nuvemshop">
            {integracoes.nuvemshop.conectado ? (
              <div className="flex flex-col gap-2 text-sm">
                <Field label="Loja" value={String(integracoes.nuvemshop.loja || "—")} />
                <Field label="Token válido" value={integracoes.nuvemshop.tokenValido ? "Sim" : "Não"} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Não conectado.</p>
            )}
          </AdminCard>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <AdminStatCard label="Pedidos importados" value={estatisticas.pedidosImportados} icon={<PackageCheck />} />
          <AdminStatCard label="Envios realizados" value={estatisticas.enviosRealizados} icon={<Truck />} />
          <AdminStatCard label="Etiquetas emitidas" value={estatisticas.etiquetasEmitidas} icon={<Tag />} />
          <AdminStatCard
            label="Último acesso"
            value={estatisticas.ultimoAcesso ? new Date(estatisticas.ultimoAcesso).toLocaleDateString("pt-BR") : "—"}
            icon={<Clock />}
          />
        </div>

        <AdminCard title="Histórico">
          {historico.length ? (
            <ul className="flex flex-col divide-y divide-slate-100">
              {historico.map((h, idx) => (
                <li key={idx} className="flex items-center justify-between text-sm py-2.5 first:pt-0 last:pb-0">
                  <span className="flex items-center gap-2">
                    <StatusBadge
                      label={h.tipo === "auditoria" ? "Ação admin" : "Sincronização"}
                      tone={h.tipo === "auditoria" ? "blue" : h.status === "error" ? "red" : "green"}
                    />
                    <span className="text-slate-700">{h.action || h.message || h.integration}</span>
                  </span>
                  <span className="text-slate-400 text-xs">{new Date(h.createdAt).toLocaleString("pt-BR")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhuma atividade registrada.</p>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
    <p className="font-medium text-slate-800">{value}</p>
  </div>
);

export default AdminClienteDetalhe;
